import { useRoute } from "@react-navigation/native";
import { uniqueId } from "lodash";
import React, { useState } from "react";
import { StyleSheet, ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useDispatch } from "react-redux";

import Container from "../../components/Container";
import Header from "../../components/Header";
import ServiceList from "../../components/ServiceList";
import { upsertService } from "../../state/consentFlow";
import { colors } from "../../theme";
import { Service, ServiceType } from "../../types";

import {
  ServiceSelectionTitle,
  ServiceSelectionDescription,
} from "./constants";

// How long the selected-card highlight shows before advancing -- long
// enough to read as "your tap registered", short enough not to feel
// like a stall.
const AUTO_ADVANCE_DELAY_MS = 350;

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.black,
    flex: 1,
  },
  scrollView: {
    paddingHorizontal: 20,
    paddingBottom: 30,
  },
});

type Props = {
  serviceType: ServiceType;
  services: Service[];
  navigation: any;
};

const SelectFromManyServices: React.FC<Props> = ({
  services,
  navigation,
  serviceType,
}) => {
  const route = useRoute();
  const dispatch = useDispatch();
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  // Auto-advances on selection instead of requiring a separate Next tap
  // right below the just-selected card -- that tight tap-then-tap
  // sequence was the exact window where iOS Safari could miss the
  // second tap (see [[project-ios-safari-double-tap]] in memory).
  const handleSelect = (service: Service) => {
    setSelectedService(service);
    setTimeout(() => {
      if ((service.informationFields || []).length > 0) {
        navigation.navigate("ServiceDetailsEntryForVisit", {
          ...route.params,
          service,
          serviceType,
        });
      } else {
        dispatch(
          upsertService({
            id: uniqueId("service"),
            serviceId: service.id,
            serviceType: serviceType.type,
            isExistingService: false,
          }),
        );

        navigation.navigate("ReviewServicesForVisit", {
          ...route.params,
        });
      }
    }, AUTO_ADVANCE_DELAY_MS);
  };

  return (
    <SafeAreaView style={styles.container} edges={["right", "bottom", "left"]}>
      <Container>
        <ScrollView style={styles.scrollView}>
          <Header
            title={ServiceSelectionTitle[serviceType.type]}
            description={ServiceSelectionDescription[serviceType.type]}
          />

          <ServiceList
            serviceType={serviceType}
            services={services}
            selectedService={selectedService}
            onSelect={handleSelect}
          />
        </ScrollView>
      </Container>
    </SafeAreaView>
  );
};

export default SelectFromManyServices;
